import type { NavVariantProps } from '~/components/PortalHeader';

import { useWindowScroll } from '@vueuse/core';

interface UseHeaderAnimationParams {
	headerHeightInPx: number;
	variants?: [NavVariantProps['variant'], NavVariantProps['variant']];
	shouldOnlyResetOnTop?: boolean;
}

interface UseHeaderAnimationReturn {
	forceFinalVariant: Ref<boolean>;
	variant: Ref<NavVariantProps['variant']>;
	transform: Ref<NavVariantProps['transform']>;
}

export function useHeaderAnimation({
	headerHeightInPx,
	variants,
	shouldOnlyResetOnTop = false
}: UseHeaderAnimationParams): UseHeaderAnimationReturn {
	const { y } = useWindowScroll();

	const [initialVariant, finalVariant] = variants ?? ['translucide', 'primary'];
	const forceFinalVariant = ref<boolean>(false);
	const transform = shallowRef<NavVariantProps['transform']>('down');
	const variant = shallowRef<NavVariantProps['variant']>(initialVariant);
	const lastNewPosition = ref(0);
	const lastOldPosition = ref<number | undefined>(0);

	function changeVariantBasedOnPosition(
		newPosition: number,
		oldPosition: number | undefined
	) {
		lastNewPosition.value = newPosition;
		lastOldPosition.value = oldPosition;
		if (forceFinalVariant.value) return;
		if (import.meta.server) return (transform.value = 'down');

		const isOnTop = newPosition === 0;

		if (newPosition < (oldPosition || 0)) {
			if (shouldOnlyResetOnTop && !isOnTop) {
				variant.value = initialVariant;
				return;
			}
			transform.value = 'down';

			variant.value = isOnTop ? initialVariant : finalVariant;
		} else if (newPosition >= headerHeightInPx && userInteracted.value) {
			transform.value = 'up';
		} else if (newPosition >= headerHeightInPx && !userInteracted.value) {
			nextTick(() => {
				variant.value = finalVariant;
				transform.value = 'up';
			});
		}
		userInteracted.value = true;
	}

	watch(forceFinalVariant, value => {
		if (value) {
			variant.value = finalVariant;
			return;
		}
		changeVariantBasedOnPosition(lastNewPosition.value, lastOldPosition.value);
	});

	const userInteracted = ref(false);

	watchThrottled(
		y,
		(newPosition, oldPosition) =>
			changeVariantBasedOnPosition(newPosition, oldPosition),
		{
			throttle: 100,
			immediate: true
		}
	);

	return {
		forceFinalVariant,
		variant: variant,
		transform: transform
	};
}
